/**
 * 브라우저 `EnvironmentAdapter`.
 *
 * 여기 있는 값들이 코어가 스스로 알 수 없는 것 전부다 — `deviceId` 생성기와 Deferred
 * Link 의 지문 재료 넷(`docs/08` §3).
 *
 * **알 수 없으면 `null` 을 준다.** 빈 문자열을 주면 서버가 "값이 있다" 로 읽어 지문 점수에
 * 드는데, 문턱이 2점이라 한 항목이 잘못 들어가는 것만으로 **남의 가구의 링크를 가져갈 수
 * 있다** (T5 의 `fingerprint.ts` 가 `hashedIp` 를 가산점에서 뺀 것과 같은 이유).
 */
export class BrowserEnvironmentAdapter {
    /**
     * UUID v4. **`crypto.randomUUID` 를 쓴다.**
     *
     * 서버 DTO 가 `@IsUUID()` 라(T2) 형식이 어긋나면 등록이 400 이다. `Math.random` 폴백을
     * 두지 않는 이유가 그것이다 — `crypto` 가 없는 환경(비보안 컨텍스트의 구형 브라우저)에서
     * 형식이 맞지 않는 id 를 만들면 **등록이 조용히 400 으로 막히고** 그 기기는 가구에
     * 들어가지 못한다. 없으면 여기서 던져서 그 사실이 드러나게 한다.
     */
    newDeviceId() {
        if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
            throw new Error('crypto.randomUUID 가 없다 — 보안 컨텍스트(HTTPS 또는 localhost)가 필요하다');
        }
        return crypto.randomUUID();
    }
    /** 웹 리시버·모바일 웹은 전부 `web` 이다. 네이티브 포팅에서 이 어댑터가 교체된다. */
    platform() {
        return 'web';
    }
    /**
     * v20. **`navigator.standalone` 하나가 곧 판정이다** — iOS 전용 비표준 속성이라
     * 그 값이 참이면 홈 화면 앱이다.
     *
     * **`(display-mode: standalone)` 을 쓰지 않는다.** 그것은 「설치되었는가」이고
     * **데스크톱 PWA 도 Android WebAPK 도 참**이다 — 그 조건으로 갈랐다가 노트북과
     * Android 에서 차례로 로그인이 막힌 적이 있다 (`docs/39` §12.4·§14).
     * 여기서 재는 것은 **iOS 의 표시 모드 하나**이고, Android 는 서버가 UA 로 가른다.
     *
     * 속성이 없으면(= iOS 가 아니면) `'browser'` 다. **`null` 은 `navigator` 자체가 없을
     * 때뿐**이고, 그때는 서버가 `unknown` 으로 그린다.
     */
    displayMode() {
        if (typeof navigator === 'undefined')
            return null;
        const standalone = navigator.standalone;
        return standalone === true ? 'standalone' : 'browser';
    }
    userAgent() {
        return typeof navigator === 'undefined' ? null : navigator.userAgent || null;
    }
    /**
     * `navigator.language` — 'ko-KR'.
     *
     * 서버는 `Accept-Language`(`ko-KR,ko;q=0.9,…`)를 정규화해 이 값과 맞춘다(T5). 여기서
     * 헤더 모양을 흉내 내지 않는다 — 정규화의 책임이 두 곳에 생기면 어긋나는 날 지문의
     * 두 점 중 하나가 통째로 사라진다.
     */
    language() {
        return typeof navigator === 'undefined' ? null : navigator.language || null;
    }
    /** IANA 타임존. `Intl` 이 없거나 실패하면 `null`. */
    timezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        }
        catch {
            return null;
        }
    }
    /**
     * '390x844'. **논리 픽셀이다** — `devicePixelRatio` 를 곱하지 않는다.
     * 곱하면 브라우저 확대·외부 디스플레이 연결로 값이 흔들려 같은 기기가 다른 지문이 된다.
     */
    screen() {
        if (typeof screen === 'undefined')
            return null;
        const { width, height } = screen;
        if (!width || !height)
            return null;
        return `${String(width)}x${String(height)}`;
    }
    /**
     * `navigator.connection` 은 표준이 아니고 사파리에 없다. 없으면 `unknown` 이다 —
     * 계약에 그 값이 있는 이유가 이것이고(`docs/08` §3), 추측해서 채우지 않는다.
     */
    networkType() {
        if (typeof navigator === 'undefined')
            return 'unknown';
        const connection = navigator
            .connection;
        if (!connection)
            return 'unknown';
        if (connection.type === 'wifi')
            return 'wifi';
        if (connection.type === 'cellular')
            return 'cellular';
        // `type` 이 없는 구현(Chromium)에서는 `effectiveType` 만 온다. 그것은 속도 등급이지
        // 매체가 아니라서 'cellular' 로 단정할 수 없다.
        return 'unknown';
    }
}
//# sourceMappingURL=browser-environment.js.map